/**
 * Loading-splash portrait resolution — defined ONCE for every surface
 * that mounts the avatar (Aug 11 2026, Keith's review: "the avatar used
 * to have a nice loading screen … now it's a plain thumbnail").
 *
 * The branded splash (AvatarLoadingSplash) shows a circular portrait +
 * "<Name> is getting ready…". The circle should show the freshest image
 * we have of the model AS CONFIGURED — the captured portrait saved from
 * the settings modal — and gracefully degrade to the static manifest
 * art. This helper builds that ordered candidate list; the splash walks
 * it with <img onError>, so a 404 from the portrait store simply falls
 * through to the next image.
 *
 * Order:
 *   1. an explicit host-resolved URL (the settings modal passes its own
 *      schema-api portrait URL with a cache-buster),
 *   2. the runtime-api portrait passthrough
 *      (`/api/avatar/portrait/resolve?tenant=&modelId=`), reachable from
 *      every SDK surface — visual IVR, hosted embeds, builder, admin,
 *   3. the manifest `portrait` (static CDN art),
 *   4. the manifest `thumbnail`.
 */

import { resolveDefaultRuntimeUrl } from '../hooks/useAvatarRuntimeConfig';
import { resolveModelUrl, type ModelConfig } from '../modelRegistry';

/** Tenant token that addresses the system level — not a real tenant. */
const SYSTEM_DEFAULT_TOKEN = '__platform-default__';

export interface LoadingPortraitOptions {
  /** The model being loaded (id + manifest art). */
  model?: Pick<ModelConfig, 'id' | 'portrait' | 'thumbnail'> | null;
  /** Runtime API base; defaults like useAvatarRuntimeConfig. */
  runtimeUrl?: string | null;
  /** Tenant project token (skipped for the system pseudo-token). */
  projectToken?: string | null;
  /** Workspace id, when the surface is workspace-scoped. */
  workspaceId?: string | null;
  /** Highest-priority explicit URL from the host. */
  explicitUrl?: string | null;
}

export function buildLoadingPortraitCandidates(options: LoadingPortraitOptions): string[] {
  const candidates: string[] = [];
  if (options.explicitUrl?.trim()) candidates.push(options.explicitUrl.trim());

  const modelId = options.model?.id?.trim();
  if (modelId) {
    const base = (options.runtimeUrl?.trim() || resolveDefaultRuntimeUrl()).replace(/\/+$/, '');
    if (base) {
      const params = new URLSearchParams();
      const tenant = options.projectToken?.trim();
      if (tenant && tenant !== SYSTEM_DEFAULT_TOKEN) {
        params.set('tenant', tenant);
        const workspaceId = options.workspaceId?.trim();
        if (workspaceId) params.set('workspace', workspaceId);
      }
      params.set('modelId', modelId);
      candidates.push(`${base}/api/avatar/portrait/resolve?${params.toString()}`);
    }
  }

  if (options.model?.portrait) candidates.push(resolveModelUrl(options.model.portrait));
  if (options.model?.thumbnail) candidates.push(resolveModelUrl(options.model.thumbnail));

  return [...new Set(candidates)];
}

export default buildLoadingPortraitCandidates;
